import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { firebaseAuth } from "../lib/firebase";
import { usePaymentPolling } from "../hooks/usePaymentPolling";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { HEALTH_CONDITIONS, BMI_MIN, BMI_MAX, PAYMENT_TYPE, SUBSCRIPTION_AMOUNT_PAISE, SUBSCRIPTION_DISPLAY_PRICE } from "../lib/constants";
import { initiatePayment, verifyPayment, loadRazorpayScript, openRazorpayCheckout } from "../services/paymentService";
import {
  ArrowRight,
  ArrowLeft,
  User,
  Target,
  Phone,
  CheckCircle2,
  Loader2,
  CreditCard,
  Gift,
} from "lucide-react";

// ── Payment feature flags (controlled via .env) ───────────────────────────────
const PAYMENT_ENABLED = process.env.REACT_APP_PAYMENT_ENABLED === "true";
const FREE_TRIAL_ENABLED = process.env.REACT_APP_FREE_TRIAL_ENABLED === "true";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/;

const extractIndianMobileDigits = (phone = "") => {
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  return "";
};

const isValidIndianMobile = (phoneDigits = "") => INDIAN_MOBILE_REGEX.test(phoneDigits);

const formatIndianMobile = (phoneDigits = "") => `+91${phoneDigits}`;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // paymentPending: true when payment is authorized but not yet captured
  // The polling useEffect will watch for subscription activation by the webhook
  const [paymentPending, setPaymentPending] = useState(false);

  // States for plan curation loader animation
  const [isCurationPhase, setIsCurationPhase] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // totalSteps: 5 when payment step is shown, otherwise 4
  // Free-trial users skip the payment step entirely (no mandate needed)
  const showPaymentStep = PAYMENT_ENABLED && !FREE_TRIAL_ENABLED;
  const totalSteps = showPaymentStep ? 5 : 4;

  // Curation countdown timer effect
  useEffect(() => {
    if (!isCurationPhase) {
      setElapsedSeconds(0);
      return;
    }
    
    const timer = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isCurationPhase]);

  // Derived properties for curation loader animation (20 seconds ETA)
  const countdown = Math.max(20 - elapsedSeconds, 1);
  const curationStep = Math.min(Math.floor(elapsedSeconds / 3.33), 5);
  const progressPercent = elapsedSeconds < 20 
    ? (elapsedSeconds / 20) * 92 
    : 92 + Math.min((elapsedSeconds - 19) * 0.5, 7); // Asymptotes at 99%


  const [heightUnit, setHeightUnit] = useState("cm");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [customCondition, setCustomCondition] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    name: user?.name || "",
    age: "",
    gender: "",
    height_cm: "",
    weight_kg: "",
    target_weight_kg: "",
    diet_preference: "",
    activity_level: "",
    health_conditions: [],
    preferred_call_time: "03:30", // 9:00 AM IST in UTC
    call_frequency: "daily",
    consent_calls: true,
    phone: extractIndianMobileDigits(user?.phone || ""),
  });

  const updateFormData = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Auto-calculate target weight based on ideal BMI (22)
      if (field === "height_cm" && value) {
        const heightCm = parseFloat(value);
        if (heightCm >= 100 && heightCm <= 250) {
          const heightMeters = heightCm / 100;
          const idealWeight = Math.round(22 * heightMeters * heightMeters);
          newData.target_weight_kg = idealWeight.toString();
        }
      }

      return newData;
    });
  };

  const handleHeightFtInChange = (ft, in_val) => {
    setHeightFt(ft);
    setHeightIn(in_val);

    const ftNum = parseFloat(ft) || 0;
    const inNum = parseFloat(in_val) || 0;

    if (ftNum > 0 || inNum > 0) {
      const cm = Math.round((ftNum * 30.48) + (inNum * 2.54));
      updateFormData("height_cm", cm.toString());
    } else {
      updateFormData("height_cm", "");
    }
  };

  const handleAddCustomCondition = () => {
    if (!customCondition.trim()) return;
    const cleanCond = customCondition.trim();
    setFormData((prev) => {
      if (prev.health_conditions.includes("None")) return prev;
      const current = prev.health_conditions;
      if (!current.includes(cleanCond)) {
        return { ...prev, health_conditions: [...current, cleanCond] };
      }
      return prev;
    });
    setCustomCondition("");
  };

  const handlePhoneChange = (value) => {
    const rawDigits = value.replace(/\D/g, "");
    let phoneDigits = rawDigits;
    if (rawDigits.startsWith("91")) {
      phoneDigits = rawDigits.slice(2);
    } else if (rawDigits.startsWith("0")) {
      phoneDigits = rawDigits.slice(1);
    }
    phoneDigits = phoneDigits.slice(0, 10);
    updateFormData("phone", phoneDigits);
  };

  const toggleHealthCondition = (condition) => {
    setFormData((prev) => {
      // If "None" is selected, clear everything else
      if (condition === "None") {
        if (prev.health_conditions.includes("None")) return { ...prev, health_conditions: [] };
        return { ...prev, health_conditions: ["None"] };
      }

      // If picking a standard condition, remove "None"
      const current = prev.health_conditions.filter(c => c !== "None");
      if (current.includes(condition)) {
        return { ...prev, health_conditions: current.filter((c) => c !== condition) };
      } else {
        return { ...prev, health_conditions: [...current, condition] };
      }
    });
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 6; h <= 22; h++) {
      for (const m of [0, 30]) {
        // Create a local date for the time slot
        const localDate = new Date();
        localDate.setHours(h, m, 0, 0);

        // Get the UTC string "HH:MM"
        const utcHour = String(localDate.getUTCHours()).padStart(2, "0");
        const utcMin = String(localDate.getUTCMinutes()).padStart(2, "0");
        const utcValue = `${utcHour}:${utcMin}`;

        // Get the local display string "H:MM AM/PM"
        const ampm = h >= 12 ? "PM" : "AM";
        const displayH = h % 12 || 12;
        const mm = String(m).padStart(2, "0");

        slots.push({
          value: utcValue,
          label: `${displayH}:${mm} ${ampm}`,
        });
      }
    }
    return slots;
  };

  // ── BMI validation helper ───────────────────────────────────────────────

  const getTargetBmiInfo = () => {
    const h = parseFloat(formData.height_cm);
    const tw = parseFloat(formData.target_weight_kg);
    if (!h || !tw || h < 100) return null;
    const bmi = tw / Math.pow(h / 100, 2);
    const minKg = Math.round(BMI_MIN * Math.pow(h / 100, 2) * 10) / 10;
    const maxKg = Math.round(BMI_MAX * Math.pow(h / 100, 2) * 10) / 10;
    const valid = bmi >= BMI_MIN && bmi <= BMI_MAX;
    return { bmi: Math.round(bmi * 10) / 10, valid, minKg, maxKg };
  };

  const targetBmiInfo = getTargetBmiInfo();

  const handleNext = () => {
    // Validate current step
    if (step === 1) {
      if (!formData.name || !formData.age || !formData.gender || !formData.phone) {
        toast.error("Please fill in all fields");
        return;
      }
      if (!isValidIndianMobile(formData.phone)) {
        toast.error("Please enter a valid 10-digit Indian mobile number");
        return;
      }
      if (parseInt(formData.age) < 10 || parseInt(formData.age) > 100) {
        toast.error("Please enter a valid age (10-100)");
        return;
      }
    } else if (step === 2) {
      if (!formData.height_cm || !formData.weight_kg || !formData.target_weight_kg) {
        toast.error("Please fill in all fields");
        return;
      }
      if (targetBmiInfo && !targetBmiInfo.valid) {
        toast.error(
          `Target weight gives an unhealthy BMI of ${targetBmiInfo.bmi}. Please choose a target between ${targetBmiInfo.minKg} kg and ${targetBmiInfo.maxKg} kg.`
        );
        return;
      }
    } else if (step === 3) {
      if (!formData.diet_preference || !formData.activity_level) {
        toast.error("Please select all options");
        return;
      }
      if (formData.health_conditions.length === 0) {
        toast.error("Please select any health conditions or 'None'");
        return;
      }
    }

    // Step 4 → if payment step is not shown (disabled or free trial), submit directly
    if (step === 4 && !showPaymentStep) {
      handleSubmit();
      return;
    }

    setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!formData.preferred_call_time) {
      toast.error("Please select your preferred call time");
      return;
    }
    if (!isValidIndianMobile(formData.phone)) {
      toast.error("Please enter a valid 10-digit Indian mobile number");
      return;
    }

    setIsCurationPhase(true);
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        phone: formatIndianMobile(formData.phone),
        age: parseInt(formData.age),
        height_cm: parseFloat(formData.height_cm),
        weight_kg: parseFloat(formData.weight_kg),
        target_weight_kg: parseFloat(formData.target_weight_kg),
      };

      const response = await axios.post(`${API_URL}/api/users/onboarding`, payload);
      updateUser(response.data.user);
      toast.success("Profile setup complete! Your diet plan is ready.");
      navigate("/dashboard");
    } catch (error) {
      let message = "Failed to save profile. Please try again.";
      const detail = error.response?.data?.detail;
      
      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail
          .map((err) => {
            const field = err.loc && err.loc.length > 1 ? err.loc.slice(1).join(".") : "field";
            return `${field}: ${err.msg}`;
          })
          .join(", ");
      } else if (detail && typeof detail === "object") {
        message = JSON.stringify(detail);
      } else if (error.message) {
        message = error.message;
      }

      toast.error(message);
      setIsCurationPhase(false);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Polling: watches for webhook-driven subscription activation ─────────
  usePaymentPolling({
    paymentPending,
    setPaymentPending,
    validStatuses: ["active", "trial"],
    onSuccess: handleSubmit,
  });

  /**
   * handlePayment — called when Step 5 "Pay & Complete Setup" is clicked.
   * 1. Creates an order on the backend (calls POST /api/payment/create-order)
   * 2. Loads the Razorpay checkout script
   * 3. Opens the Razorpay modal
   * 4. On payment success, submits onboarding
   */
  const handlePayment = async () => {
    setIsLoading(true);
    try {
      const token = await firebaseAuth.currentUser?.getIdToken();

      // Initiate payment: creates a Razorpay Order (one_time) or Subscription
      const paymentData = await initiatePayment(token, PAYMENT_TYPE, {
        // one_time options (ignored for subscription)
        amountPaise: SUBSCRIPTION_AMOUNT_PAISE,
        receipt: `onboarding_${user.uid}_${Date.now()}`,
      });

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment SDK. Please check your internet connection.");
        setIsLoading(false);
        return;
      }

      setIsLoading(false); // release loading while modal is open

      openRazorpayCheckout(
        PAYMENT_TYPE,
        paymentData,
        {
          name: formData.name,
          email: user.email || "",
          contact: `+91${formData.phone}`,
        },
        async (razorpayResponse) => {
          // Razorpay handler fired — verify signature on backend
          setIsLoading(true);
          try {
            const freshToken = await firebaseAuth.currentUser?.getIdToken(true);
            const result = await verifyPayment(freshToken, PAYMENT_TYPE, razorpayResponse);

            if (
              result.payment_status === "captured" ||
              result.payment_status === "active"
            ) {
              // Payment confirmed — complete onboarding
              toast.success("Payment confirmed! Setting up your plan…");
              handleSubmit();
            } else {
              // One-time payment authorised but not yet captured — start polling
              setIsLoading(false);
              setPaymentPending(true);
            }
          } catch (verifyErr) {
            toast.error(verifyErr.message || "Payment verification failed. Please contact support.");
            setIsLoading(false);
          }
        },
        (err) => {
          toast.error(err.message || "Payment failed. Please try again.");
        }
      );
    } catch (err) {
      toast.error(err.message || "Could not initiate payment. Please try again.");
      setIsLoading(false);
    }
  };

  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4 border-b border-border/50">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-10 h-10 object-contain" />
            </div>
            <span className="font-bold text-lg text-foreground">Nutri Saathi</span>
          </div>
          {!isCurationPhase && (
            <span className="text-sm text-muted-foreground">
              Step {step} of {totalSteps}
            </span>
          )}
        </div>
      </header>

      {/* Progress Bar */}
      <div className="w-full bg-secondary/50">
        <Progress value={isCurationPhase ? progressPercent : progress} className="h-1 rounded-none transition-all duration-1000" />
      </div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          {isCurationPhase ? (
            <Card className="border-border/50 shadow-lg p-8 space-y-8 fade-in text-center max-w-md mx-auto bg-card rounded-2xl">
              <div className="relative w-32 h-32 mx-auto flex items-center justify-center">
                {/* SVG Progress Circle */}
                <svg viewBox="0 0 128 128" className="w-full h-full transform -rotate-90">
                  {/* Track */}
                  <circle
                    cx="64"
                    cy="64"
                    r="45"
                    className="stroke-muted"
                    strokeWidth="5"
                    fill="transparent"
                  />
                  {/* Progress Indicator */}
                  <circle
                    cx="64"
                    cy="64"
                    r="45"
                    stroke="hsl(var(--accent))"
                    strokeWidth="5"
                    fill="transparent"
                    strokeDasharray="282.74"
                    strokeDashoffset={282.74 - (progressPercent / 100) * 282.74}
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-out"
                  />
                </svg>
                {/* Inner countdown display */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                  {countdown > 1 ? (
                    <>
                      <span className="text-3xl font-bold text-foreground leading-none mb-1">
                        {countdown}s
                      </span>
                      <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest leading-none">
                        ETA
                      </span>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="w-6 h-6 text-accent animate-spin mb-1.5" />
                      <span className="text-[8px] text-muted-foreground uppercase font-bold tracking-widest text-center leading-normal max-w-[80px]">
                        Almost Ready
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-2xl font-bold text-foreground">Curating Your Plan</h3>
                <p className="text-sm text-muted-foreground">
                  Our AI is curating your personalized Indian diet plan based on your metrics & preferences.
                </p>
              </div>

              {/* Checklist */}
              <div className="space-y-4 text-left border-t border-border/50 pt-6">
                {[
                  "Analyzing body metrics & target BMI",
                  "Calculating daily calorie & macro targets",
                  `Tailoring recipes for ${formData.diet_preference === "veg" ? "Vegetarian" : formData.diet_preference === "vegan" ? "Vegan" : "Non-Vegetarian"} diet`,
                  "Structuring accountability call schedule",
                  "Generating personalized AI diet plan",
                  "Finalizing your wellness dashboard",
                ].map((label, idx) => {
                  const isDone = curationStep > idx;
                  const isActive = curationStep === idx;
                  
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 transition-opacity duration-300"
                      style={{ opacity: isDone || isActive ? 1 : 0.4 }}
                    >
                      <div className="flex items-center justify-center w-5 h-5 shrink-0">
                        {isDone ? (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        ) : isActive ? (
                          <Loader2 className="w-4 h-4 text-accent animate-spin" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-muted" />
                        )}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          isActive
                            ? "text-primary font-semibold"
                            : isDone
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="p-4 bg-secondary/30 rounded-xl flex items-center justify-center gap-3">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-semibold text-emerald-800">
                  {countdown > 1 ? "Creating your customized profile..." : "Almost ready, compiling details..."}
                </span>
              </div>
            </Card>
          ) : (
            <>
              {/* Step 1: Basic Info */}
              {step === 1 && (
            <Card className="border-border/50 shadow-lg fade-in">
              <CardHeader className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <User className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-2xl">Let's get to know you</CardTitle>
                <CardDescription>
                  Tell us a bit about yourself so we can personalize your experience
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => updateFormData("name", e.target.value)}
                    className="input-wellness"
                    data-testid="onboarding-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="input-wellness border px-3 flex items-center gap-2 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20">
                    <span className="text-lg leading-none" aria-hidden="true">🇮🇳</span>
                    <span className="text-sm font-medium text-foreground">+91</span>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      placeholder="9876543210"
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      className="flex-1 h-full border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none focus:ring-0"
                      maxLength={10}
                      data-testid="onboarding-phone-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Enter your 10-digit Indian mobile number.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="age">Age</Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="Your age"
                    value={formData.age}
                    onChange={(e) => updateFormData("age", e.target.value)}
                    className="input-wellness"
                    min="10"
                    max="100"
                    data-testid="onboarding-age-input"
                  />
                </div>

                <div className="space-y-3">
                  <Label>Gender</Label>
                  <RadioGroup
                    value={formData.gender}
                    onValueChange={(value) => updateFormData("gender", value)}
                    className="flex gap-4"
                  >
                    {[
                      { value: "male", label: "Male" },
                      { value: "female", label: "Female" },
                      { value: "other", label: "Other" },
                    ].map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={option.value}
                          id={`gender-${option.value}`}
                          data-testid={`onboarding-gender-${option.value}`}
                        />
                        <Label htmlFor={`gender-${option.value}`} className="cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Body Metrics */}
          {step === 2 && (
            <Card className="border-border/50 shadow-lg fade-in">
              <CardHeader className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-2xl">Your Body & Goals</CardTitle>
                <CardDescription>
                  This helps us create your personalized diet plan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="height">Height</Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setHeightUnit("cm")}
                        className={`text-xs px-2 py-1 rounded-md transition-colors ${heightUnit === "cm" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}
                      >
                        cm
                      </button>
                      <button
                        type="button"
                        onClick={() => setHeightUnit("ft")}
                        className={`text-xs px-2 py-1 rounded-md transition-colors ${heightUnit === "ft" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}
                      >
                        ft/in
                      </button>
                    </div>
                  </div>

                  {heightUnit === "cm" ? (
                    <Input
                      id="height"
                      type="number"
                      placeholder="e.g., 170"
                      value={formData.height_cm}
                      onChange={(e) => updateFormData("height_cm", e.target.value)}
                      className="input-wellness"
                      min="100"
                      max="250"
                      data-testid="onboarding-height-input"
                    />
                  ) : (
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          id="height-ft"
                          type="number"
                          placeholder="ft"
                          value={heightFt}
                          onChange={(e) => handleHeightFtInChange(e.target.value, heightIn)}
                          className="input-wellness"
                          min="3"
                          max="8"
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          id="height-in"
                          type="number"
                          placeholder="in"
                          value={heightIn}
                          onChange={(e) => handleHeightFtInChange(heightFt, e.target.value)}
                          className="input-wellness"
                          min="0"
                          max="11"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weight">Current Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="e.g., 70"
                    value={formData.weight_kg}
                    onChange={(e) => updateFormData("weight_kg", e.target.value)}
                    className="input-wellness"
                    min="30"
                    max="300"
                    data-testid="onboarding-weight-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="target-weight">Target Weight (kg)</Label>
                    {parseFloat(formData.height_cm) >= 100 && (
                      <span className="text-xs text-muted-foreground">
                        Ideal: {Math.round(22 * Math.pow(parseFloat(formData.height_cm) / 100, 2))} kg (BMI 22)
                      </span>
                    )}
                  </div>
                  <Input
                    id="target-weight"
                    type="number"
                    placeholder="e.g., 65"
                    value={formData.target_weight_kg}
                    onChange={(e) => updateFormData("target_weight_kg", e.target.value)}
                    className={`input-wellness ${targetBmiInfo && !targetBmiInfo.valid ? "border-red-400 focus:ring-red-400" : targetBmiInfo?.valid ? "border-green-400" : ""}`}
                    min="30"
                    max="300"
                    data-testid="onboarding-target-weight-input"
                  />
                  {targetBmiInfo && (
                    <div className={`flex items-start gap-2 px-3 py-2 rounded-xl text-xs ${targetBmiInfo.valid
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                      }`}>
                      <span className="mt-0.5">{targetBmiInfo.valid ? "✅" : "⚠️"}</span>
                      <span>
                        {targetBmiInfo.valid
                          ? `BMI ${targetBmiInfo.bmi} — healthy range ✓`
                          : `BMI ${targetBmiInfo.bmi} is outside the healthy range (${BMI_MIN}–${BMI_MAX}). For your height, choose a target between ${targetBmiInfo.minKg} kg and ${targetBmiInfo.maxKg} kg.`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Diet & Activity */}
          {step === 3 && (
            <Card className="border-border/50 shadow-lg fade-in">
              <CardHeader className="text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center mb-4">
                  <img src="/logo.svg" alt="Lifestyle" className="w-10 h-10 object-contain" />
                </div>
                <CardTitle className="text-2xl">Lifestyle Preferences</CardTitle>
                <CardDescription>
                  Help us understand your eating habits and activity level
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label>Diet Preference</Label>
                  <RadioGroup
                    value={formData.diet_preference}
                    onValueChange={(value) => updateFormData("diet_preference", value)}
                    className="grid grid-cols-3 gap-3"
                  >
                    {[
                      { value: "veg", label: "Vegetarian" },
                      { value: "non-veg", label: "Non-Veg" },
                      { value: "vegan", label: "Vegan" },
                    ].map((option) => (
                      <div key={option.value} className="relative">
                        <RadioGroupItem
                          value={option.value}
                          id={`diet-${option.value}`}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={`diet-${option.value}`}
                          data-testid={`onboarding-diet-${option.value}`}
                          className="flex flex-col items-center justify-center rounded-xl border-2 border-border bg-white p-4 cursor-pointer hover:border-primary/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-all"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label>Activity Level</Label>
                  <Select
                    value={formData.activity_level}
                    onValueChange={(value) => updateFormData("activity_level", value)}
                  >
                    <SelectTrigger className="input-wellness" data-testid="onboarding-activity-select">
                      <SelectValue placeholder="Select your activity level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedentary">Sedentary (desk job, little exercise)</SelectItem>
                      <SelectItem value="light">Light (light exercise 1-3 days/week)</SelectItem>
                      <SelectItem value="moderate">Moderate (exercise 3-5 days/week)</SelectItem>
                      <SelectItem value="active">Active (hard exercise 6-7 days/week)</SelectItem>
                      <SelectItem value="very_active">Very Active (physical job + exercise)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>


                <div className="space-y-3">
                  <Label>Health Conditions</Label>
                  <p className="text-sm text-muted-foreground">Select any that apply</p>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(new Set([...HEALTH_CONDITIONS, ...formData.health_conditions]))
                      .sort((a, b) => a === "None" ? 1 : b === "None" ? -1 : 0)
                      .map((condition) => {
                        const isSelected = formData.health_conditions.includes(condition);
                        return (
                          <button
                            key={condition}
                            type="button"
                            onClick={() => toggleHealthCondition(condition)}
                            className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${isSelected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-secondary/50 text-foreground border-border hover:border-primary/50"
                              }`}
                          >
                            {condition}
                          </button>
                        );
                      })}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Other condition..."
                      value={customCondition}
                      onChange={(e) => setCustomCondition(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCustomCondition();
                        }
                      }}
                      className="input-wellness flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddCustomCondition}
                      disabled={formData.health_conditions.includes("None") || !customCondition.trim()}
                    >
                      Add Custom
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Call Schedule */}
          {step === 4 && (
            <Card className="border-border/50 shadow-lg fade-in">
              <CardHeader className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Phone className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-2xl">Accountability Calls</CardTitle>
                <CardDescription>
                  Set up your AI accountability calls to stay on track
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Preferred Call Time</Label>
                  <Select
                    value={formData.preferred_call_time}
                    onValueChange={(value) => updateFormData("preferred_call_time", value)}
                  >
                    <SelectTrigger className="input-wellness" data-testid="onboarding-call-time-select">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {generateTimeSlots().map((slot) => (
                        <SelectItem key={slot.value} value={slot.value}>
                          {slot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Call Frequency</Label>
                  <RadioGroup
                    value={formData.call_frequency}
                    onValueChange={(value) => updateFormData("call_frequency", value)}
                    className="grid grid-cols-2 gap-3"
                  >
                    {[
                      { value: "daily", label: "Daily", desc: "Every day" },
                      { value: "alternate", label: "Alternate Days", desc: "Every other day" },
                    ].map((option) => (
                      <div key={option.value} className="relative">
                        <RadioGroupItem
                          value={option.value}
                          id={`frequency-${option.value}`}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={`frequency-${option.value}`}
                          data-testid={`onboarding-frequency-${option.value}`}
                          className="flex flex-col items-center justify-center rounded-xl border-2 border-border bg-white p-4 cursor-pointer hover:border-primary/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-all"
                        >
                          <span className="font-semibold">{option.label}</span>
                          <span className="text-sm text-muted-foreground">{option.desc}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-secondary/30 rounded-xl">
                  <Checkbox
                    id="consent"
                    checked={formData.consent_calls}
                    onCheckedChange={(checked) => updateFormData("consent_calls", checked)}
                    data-testid="onboarding-consent-checkbox"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="consent" className="cursor-pointer font-medium">
                      I agree to receive AI accountability calls
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      You can opt-out anytime from your dashboard settings.
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-800">
                    <strong>Disclaimer:</strong> This is not medical advice.
                    Please consult a healthcare professional for personalized medical guidance.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Payment — only rendered when payment step is active (not free trial) */}
          {showPaymentStep && step === 5 && (
            <Card className="border-border/50 shadow-lg fade-in">

              {/* ── Pending capture: waiting for webhook confirmation ── */}
              {paymentPending ? (
                <>
                  <CardHeader className="text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                      <Loader2 className="w-7 h-7 text-primary animate-spin" />
                    </div>
                    <CardTitle className="text-2xl">Confirming Your Payment</CardTitle>
                    <CardDescription>
                      Your payment was received. We're waiting for the bank to confirm the transfer — this usually takes a few seconds.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 text-center space-y-2">
                      <p className="text-sm font-medium text-foreground">Please keep this page open</p>
                      <p className="text-xs text-muted-foreground">
                        We'll automatically take you to your dashboard as soon as confirmation arrives.
                        You don't need to do anything.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/30">
                      <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        If this takes more than a few minutes, please contact support with your payment ID.
                      </p>
                    </div>
                  </CardContent>
                </>

              ) : (
                /* ── Normal Step 5: paid subscription ── */
                <>
                  <CardHeader className="text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                      <CreditCard className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Complete Your Subscription</CardTitle>
                    <CardDescription>
                      Unlock AI coaching calls, personalised diet plans, and more.
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* ── Paid Subscription Card ── */}
                    <div className="space-y-4">
                      <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 text-center space-y-1">
                        <p className="text-4xl font-bold text-primary">{SUBSCRIPTION_DISPLAY_PRICE}</p>
                        <p className="text-sm text-muted-foreground">per month · cancel anytime</p>
                      </div>
                      <ul className="space-y-2 text-sm text-foreground">
                        {[
                          "Personalised AI diet plan",
                          "Daily accountability calls",
                          "Real-time nutrition tracking",
                          "Priority support",
                        ].map((feature) => (
                          <li key={feature} className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-center text-muted-foreground">
                        Secured by Razorpay · 256-bit SSL encryption
                      </p>
                    </div>
                  </CardContent>
                </>
              )}
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={handleBack}
                className="rounded-xl px-6"
                disabled={isLoading}
                data-testid="onboarding-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {/* Steps 1–3: always show Continue */}
            {step < 4 && (
              <Button
                onClick={handleNext}
                className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl px-6 btn-primary"
                data-testid="onboarding-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}

            {/* Step 4: if payment step is shown → Continue to payment;
                        if free trial → Start Free Trial;
                        else → Complete Setup */}
            {step === 4 && showPaymentStep && (
              <Button
                onClick={handleNext}
                className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl px-6 btn-primary"
                data-testid="onboarding-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {step === 4 && FREE_TRIAL_ENABLED && (
              <Button
                onClick={handleSubmit}
                className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl px-6 btn-primary"
                disabled={isLoading}
                data-testid="onboarding-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Setting up your plan...
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    Start Free Trial
                  </>
                )}
              </Button>
            )}
            {step === 4 && !PAYMENT_ENABLED && !FREE_TRIAL_ENABLED && (
              <Button
                onClick={handleSubmit}
                className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl px-6 btn-primary"
                disabled={isLoading}
                data-testid="onboarding-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating your plan...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Complete Setup
                  </>
                )}
              </Button>
            )}

            {/* Step 5 (Payment): only shown when showPaymentStep=true */}
            {showPaymentStep && step === 5 && (
              <Button
                onClick={handlePayment}
                className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl px-6 btn-primary"
                disabled={isLoading}
                data-testid="onboarding-pay-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Preparing payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Complete Setup
                  </>
                )}
              </Button>
            )}
          </div>
            </>
          )}
        </div>
      </main >
    </div >
  );
};

export default OnboardingPage;
